<template>
  <div class="home-container">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">生产级 SRE 实战练兵场</h1>
        <p class="hero-subtitle">
          一键开启高可用集群、大规模资源调度与关键基础设施仿真环境。<br>
          助力工程师提升复杂生产环境下的架构设计与应急响应能力。
        </p>
        <div class="hero-actions">
          <el-button type="primary" size="large" @click="$router.push('/multi-instances')">开启实战环境</el-button>
        </div>
      </div>
      <div class="terminal-window">
        <div class="terminal-header">
          <span class="dot red"></span>
          <span class="dot yellow"></span>
          <span class="dot green"></span>
          <span class="terminal-title">sre@azlinux.com: ~</span>
        </div>
        <div class="terminal-body">
          <div class="terminal-line" :class="`line-${line.type}`" v-for="(line, index) in terminalLines" :key="index">
            <span v-if="line.type === 'command'" class="prompt">sre@azlinux.com:~$</span>
            <span>{{ line.text }}</span>
          </div>
          <div v-if="showInput" class="terminal-line current">
            <span class="prompt">sre@azlinux.com:~$</span>
            <span>{{ currentTyping }}</span><span class="cursor">_</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Capabilities -->
    <section class="capabilities-section">
      <div class="section-header">
        <h2>核心实战场景</h2>
        <p>覆盖金融、能源、电商、电信级高标准运维要求，草台班子环境提升</p>
      </div>

      <el-row :gutter="30">
        <el-col :span="12" v-for="(item, index) in capabilityItems" :key="index">
          <el-card class="cap-card" :body-style="{ padding: '0px' }" shadow="hover">
            <div class="cap-image-wrapper">
              <el-image :src="item.image" fit="cover" class="cap-image" />
              <div class="cap-overlay">
                <i :class="item.icon"></i>
              </div>
            </div>
            <div class="cap-info">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <!-- Trust Badges / Footer Quote -->
    <section class="footer-quote">
      <div class="quote-content">
        <p>“科学的唯一目的，就是减轻人类生存的苦难。”</p>
        <span class="quote-author">— 赋能每一位追求卓越的运维工程师</span>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      terminalLines: [],
      fullText: "kubectl get po",
      currentTyping: "",
      showInput: true,
      animationTimers: [],
      animationStopped: false,
      capabilityItems: [
        {
          title: "大规模资源调度",
          desc: "模拟千台级虚拟机与万级POD调度场景，演练极端资源压力下的弹性伸缩与负载均衡策略。",
          image: "https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/pic01.jpg",
          icon: "el-icon-cpu"
        },
        {
          title: "高可用架构演练",
          desc: "涵盖多活机房切换、数据库强一致性保障，实战演练业务零中断的高可用架构方案。",
          image: "https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/pic02.jpg",
          icon: "el-icon-connection"
        },
        {
          title: "关键基础设施应急",
          desc: "脱敏还原能源、政务等行业真实故障，演练限时故障诊断与业务快速恢复全流程。",
          image: "https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/pic03.jpg",
          icon: "el-icon-warning-outline"
        },
        {
          title: "敏捷POC环境",
          desc: "1分钟快速拉起复杂测试环境，支持多架构部署验证，全球区域可选，提升演示效率。",
          image: "https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/pic04.jpg",
          icon: "el-icon-aim"
        }
      ]
    };
  },
  mounted() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.showCompleteTerminal();
    } else {
      this.startTerminalAnimation();
    }
  },
  beforeUnmount() {
    this.animationStopped = true;
    this.animationTimers.forEach(timer => window.clearTimeout(timer));
  },
  methods: {
    terminalOutput() {
      return [
        { type: "heading", text: "NAME                              READY   STATUS    RESTARTS   AGE" },
        { type: "output", text: "nginx-7c9d6f8b5c-4kp7m              1/1     Running   0          46s" },
        { type: "output", text: "nginx-7c9d6f8b5c-r8n2q              1/1     Running   0          46s" },
        { type: "output", text: "gateway-6f7b9d84c5-d2x6p            1/1     Running   0          43s" },
        { type: "output", text: "gateway-6f7b9d84c5-m9w4k            1/1     Running   0          43s" },
        { type: "output", text: "order-5d8f6c7b49-b3h7s              1/1     Running   6          39s" },
        { type: "output", text: "order-5d8f6c7b49-v6j2n              1/1     Running   0          39s" },
        { type: "warning", text: "⚠ order 服务发生 6 次重启，故障线索等待你定位。" },
        { type: "success", text: "✓ 企业级故障环境和练习环境快速就绪，现在轮到你接管，出手吧。" },
        { type: "slogan", text: "少看一次教程，多解决一次真实问题。" },
      ];
    },
    wait(delay) {
      return new Promise(resolve => {
        const timer = window.setTimeout(() => {
          this.animationTimers = this.animationTimers.filter(item => item !== timer);
          resolve();
        }, delay);
        this.animationTimers.push(timer);
      });
    },
    showCompleteTerminal() {
      this.showInput = false;
      this.currentTyping = "";
      this.terminalLines = [{ type: "command", text: this.fullText }, ...this.terminalOutput()];
    },
    async startTerminalAnimation() {
      while (!this.animationStopped) {
        this.terminalLines = [];
        this.currentTyping = "";
        this.showInput = true;
        for (let index = 1; index <= this.fullText.length && !this.animationStopped; index += 1) {
          this.currentTyping = this.fullText.slice(0, index);
          await this.wait(65);
        }
        if (this.animationStopped) return;
        this.terminalLines.push({ type: "command", text: this.fullText });
        this.currentTyping = "";
        this.showInput = false;
        await this.wait(240);
        for (const line of this.terminalOutput()) {
          if (this.animationStopped) return;
          this.terminalLines.push(line);
          await this.wait(line.type === "slogan" ? 420 : 170);
        }
        await this.wait(5200);
      }
    }
  }
};
</script>

<style scoped>
.home-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: left;
}

/* Hero Section */
.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 80px 0;
  gap: 60px;
}

.hero-content {
  flex: 1;
}

.hero-title {
  font-size: 48px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 24px;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 18px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 40px;
}

.hero-actions {
  display: flex;
  gap: 16px;
}

/* Terminal Window */
.terminal-window {
  flex: 1;
  background: #1e1e1e;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  font-family: "Fira Code", monospace;
  max-width: 550px;
}

.terminal-header {
  background: #333;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.red {
  background: #ff5f56;
}

.yellow {
  background: #ffbd2e;
}

.green {
  background: #27c93f;
}

.terminal-title {
  color: #9ca3af;
  font-size: 12px;
  margin-left: 8px;
}

.terminal-body {
  padding: 20px;
  color: #d4d4d4;
  font-size: 14px;
  min-height: 390px;
  overflow: hidden;
}

.terminal-line {
  margin-bottom: 8px;
  line-height: 1.5;
  white-space: pre;
  animation: terminal-in .18s ease-out;
}

.prompt {
  color: #10b981;
  margin-right: 8px;
  font-weight: 600;
}

.line-heading {
  color: #93c5fd;
  font-size: 12px;
}

.line-output {
  color: #d1d5db;
  font-size: 12px;
}

.line-success {
  color: #34d399;
  margin-top: 16px;
}

.line-warning {
  color: #fb923c;
  margin-top: 14px;
}

.line-slogan {
  color: #fbbf24;
  font-weight: 700;
  margin-top: 10px;
}

.cursor {
  animation: blink 1s infinite;
  color: #10b981;
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

@keyframes terminal-in {
  from { opacity: 0; transform: translateY(3px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .terminal-line, .cursor { animation: none; }
}

/* Capabilities Section */
.capabilities-section {
  padding: 80px 0;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 32px;
  color: #111827;
  margin-bottom: 12px;
}

.section-header p {
  color: #6b7280;
  font-size: 16px;
}

.cap-card {
  margin-bottom: 30px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
}

.cap-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2; /* 适配 600x400 图片比例 */
  overflow: hidden;
  background: #f3f4f6;
}

.cap-image {
  width: 100%;
  height: 100%;
  display: block;
}

.cap-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(37, 99, 235, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.cap-card:hover .cap-overlay {
  opacity: 1;
}

.cap-overlay i {
  font-size: 48px;
  color: #fff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.cap-info {
  padding: 24px;
}

.cap-info h3 {
  font-size: 20px;
  margin: 0 0 12px;
  color: #111827;
}

.cap-info p {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
}

/* Footer Quote */
.footer-quote {
  padding: 80px 0 120px;
  text-align: center;
}

.quote-content p {
  font-size: 24px;
  font-style: italic;
  color: #374151;
  margin-bottom: 16px;
  font-family: "Georgia", serif;
}

.quote-author {
  color: #9ca3af;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 992px) {
  .hero-section {
    flex-direction: column;
    text-align: center;
    padding: 40px 0;
  }

  .hero-actions {
    justify-content: center;
  }

  .terminal-window {
    margin: 0 auto;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 32px;
  }

  .el-col {
    width: 100% !important;
  }

  .terminal-body {
    padding: 16px;
    font-size: 12px;
    overflow-x: auto;
  }
}
</style>
